import { useState } from 'react';
import {
  Box, Button, Stack, TextField, MenuItem, Typography, CircularProgress,
  useMediaQuery, useTheme,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { Calculate as CalcIcon } from '@mui/icons-material';
import { useBeneficiosStore } from '../store/beneficiosStore';

const currentYear = new Date().getFullYear();

export default function GratificacionPanel() {
  const { gratResultados, loading, calcularGratificacion } = useBeneficiosStore();
  const [semestre, setSemestre] = useState(1);
  const [anio, setAnio] = useState(currentYear);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleCalcular = () => calcularGratificacion(semestre, anio);

  const columns: GridColDef[] = [
    { field: 'nombreTrabajador', headerName: 'Trabajador', flex: 1, minWidth: 150 },
    ...(!isMobile ? [
      { field: 'remuneracionComputable', headerName: 'Rem. Comp.', width: 130, type: 'number' as const,
        valueFormatter: (value: number) => value?.toFixed(2) },
      { field: 'mesesComputables', headerName: 'Meses', width: 80, type: 'number' as const },
    ] : []),
    { field: 'montoGratificacion', headerName: isMobile ? 'Grat.' : 'Gratificación', width: isMobile ? 100 : 130, type: 'number' as const,
      valueFormatter: (value: number) => value?.toFixed(2) },
    ...(!isMobile ? [
      { field: 'bonificacionExtraordinaria', headerName: 'Bonif. Extra (9%)', width: 140, type: 'number' as const,
        valueFormatter: (value: number) => value?.toFixed(2) },
    ] : []),
    { field: 'totalBruto', headerName: 'Total', width: isMobile ? 100 : 130, type: 'number' as const,
      valueFormatter: (value: number) => value?.toFixed(2),
      cellClassName: 'font-bold' },
    ...(!isMobile ? [
      { field: 'detalleCalculo', headerName: 'Detalle', flex: 1, minWidth: 250 },
    ] : []),
  ];

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1.5}>
          <TextField
            select size="small" label="Semestre" value={semestre}
            onChange={(e) => setSemestre(Number(e.target.value))} sx={{ width: { xs: '100%', sm: 180 }, flex: { xs: 1, sm: 'none' } }}
          >
            <MenuItem value={1}>Sem 1 Ene-Jun (Julio)</MenuItem>
            <MenuItem value={2}>Sem 2 Jul-Dic (Diciembre)</MenuItem>
          </TextField>
          <TextField
            size="small" label="Año" type="number" value={anio}
            onChange={(e) => setAnio(Number(e.target.value))} sx={{ width: 100 }}
          />
        </Stack>
        <Button variant="contained" startIcon={loading ? <CircularProgress size={18} /> : <CalcIcon />}
          onClick={handleCalcular} disabled={loading} size={isMobile ? 'small' : 'medium'} fullWidth={isMobile}>
          {isMobile ? 'Calcular' : 'Calcular Gratificación'}
        </Button>
      </Stack>

      {gratResultados.length > 0 && (
        <>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            Gratificación {semestre === 1 ? 'Julio' : 'Diciembre'} {anio}
          </Typography>
          <DataGrid
            rows={gratResultados}
            columns={columns}
            getRowId={(row) => row.idTrabajador}
            autoHeight
            density="compact"
            disableRowSelectionOnClick
            pageSizeOptions={[25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 3 }} sx={{ mt: 1 }}>
            <Typography variant="body2">
              Total Grat.: S/ {gratResultados.reduce((s, r) => s + r.montoGratificacion, 0).toFixed(2)}
            </Typography>
            <Typography variant="body2">
              Total Bonif.: S/ {gratResultados.reduce((s, r) => s + r.bonificacionExtraordinaria, 0).toFixed(2)}
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              Total Bruto: S/ {gratResultados.reduce((s, r) => s + r.totalBruto, 0).toFixed(2)}
            </Typography>
          </Stack>
        </>
      )}
    </Box>
  );
}
